import Fetcher from '../common/Fetcher';
import PrestaGlobals from '../common/PrestaGlobals';
import ICabinetResult from '../unityView/interfaces/ICabinetResult';

/**
 * Thin client for the employee-gated `ucadmin` front controller (M3 persistence).
 * Reads/writes go here; the read-only db.php path is unchanged. Every call is re-authorized
 * server-side, so a non-employee just gets a 403 JSON error.
 */
const base = (): string =>
    PrestaGlobals.virtualUri + '/index.php?fc=module&module=ubercabinet&controller=ucadmin';

const enc = (obj: Record<string, string | number>): string =>
    Object.entries(obj)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join('&');

async function get<T = any>(action: string, params: Record<string, string | number> = {}): Promise<T | null> {
    const f = new Fetcher<T>(`${base()}&action=${action}` + (Object.keys(params).length ? `&${enc(params)}` : ''));
    await f.request();
    return f.Result;
}

async function post<T = any>(action: string, body: Record<string, string | number>): Promise<T | null> {
    const f = new Fetcher<T>(`${base()}&action=${action}`, 'POST');
    await f.request(enc(body));
    return f.Result;
}

export interface IScriptRef { id: number; name: string; }
export interface IProductRef { id_product: number; name: string | null; }
export interface IPropertyRef { id: number; property: string; type: string; }
export interface ITargetTypeRef { id: number; name: string; }
export interface ICreateParamInput {
    name: string; property: string; target_path?: string; target_type?: string;
    descriptor?: string; value?: string; tab?: string;
}

const AdminApi = {
    listScripts: () => get<IScriptRef[]>('listScripts'),
    getScriptInfo: (idProduct: number) => get<IScriptRef | { error: string }>('getScriptInfo', { id_product: idProduct }),
    productsUsingScript: (idScript: number) => get<IProductRef[]>('productsUsingScript', { id_configuration_script: idScript }),
    updateScript: (idScript: number, script: string) =>
        post<{ success: boolean }>('updateScript', { id_configuration_script: idScript, script }),
    saveScriptAsNew: (name: string, script: string, idProduct?: number, assign?: boolean) =>
        post<{ success: boolean; id_configuration_script: number; assigned: boolean }>('saveScriptAsNew', {
            name, script,
            ...(idProduct ? { id_product: idProduct } : {}),
            ...(assign ? { assign: 1 } : {}),
        }),
    assignScript: (idProduct: number, idScript: number) =>
        post<{ success: boolean }>('assignScript', { id_product: idProduct, id_configuration_script: idScript }),
    /** Persist a parameter value as the current shop's default. `id` is uc_style_product_parameters.id. */
    saveParamValue: (id: string, idProduct: number, value: string) =>
        post<{ success: boolean; style_parameter_id: number; id_shop: number }>('saveParamValue', {
            id, id_product: idProduct, value,
        }),
    listProperties: () => get<IPropertyRef[]>('listProperties'),
    listTargetTypes: () => get<ITargetTypeRef[]>('listTargetTypes'),
    createParam: (idProduct: number, input: ICreateParamInput) =>
        post<{ success: boolean; id: number; style_parameter_id: number; valueSaved: boolean } | { error: string }>(
            'createParam', { id_product: idProduct, ...input } as Record<string, string | number>,
        ),
    /** Delete a product's parameter row (cleans up orphaned definition + values server-side). */
    deleteParam: (id: string, idProduct: number) =>
        post<{ success: boolean }>('deleteParam', { id, id_product: idProduct }),
    /** EA-446: create a new configurable product from an existing one (same script + params, current shop). */
    createProduct: (srcProductId: number, name: string) =>
        post<{ success: boolean; id_product: number; id_configuration_script: number } | { error: string }>(
            'createProduct', { id_product: srcProductId, name }),
    /** Persist a parameter DEFINITION (descriptor + target/tab/title/panel/sort). Identity (name/property) unchanged. */
    updateParamDef: (id: string, idProduct: number, fields: {
        descriptor: string; target_path?: string; target_path_null?: string; target_type?: string;
        tab?: string; title?: string; panel?: string; sort_index?: string;
    }) => post<{ success: boolean } | { error: string }>(
        'updateParamDef', { id, id_product: idProduct, ...fields } as Record<string, string | number>,
    ),
    /**
     * Where the AI designer service is and a short-lived token for its WebSocket, signed for the
     * logged-in employee. `token` is null when the shop has no UC_AI_DESIGNER_SECRET (a service
     * started with --insecure still accepts the page then).
     */
    aiDesignerSession: () =>
        get<{ url: string; token: string | null; expires: number } | { error: string }>('aiDesignerSession'),
    /**
     * The design the Designer shows, a zip of its parts' manufacturing files in `format` (one of
     * UcDxfExport::FORMATS: each part's raw and finished stage), made from the order Unity built
     * for it. A file rather than JSON, so not through Fetcher.
     */
    dxfZip: async (order: ICabinetResult, format: string = 'dxf'): Promise<{ blob: Blob; fileName: string } | { error: string }> => {
        try {
            const response = await fetch(`${base()}&action=dxfZip`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: enc({ order: JSON.stringify(order), format }),
            });
            if (!response.ok || !(response.headers.get('Content-Type') ?? '').includes('zip')) {
                const json = await response.json().catch(() => null);
                return { error: json?.error ?? `HTTP ${response.status}` };
            }
            const disposition = response.headers.get('Content-Disposition') ?? '';
            return { blob: await response.blob(), fileName: /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'dxf.zip' };
        } catch (e) {
            return { error: String(e) };
        }
    },
};

export default AdminApi;
